/**
 * Rapor dili.
 *
 * Skorları insan cümlesine çeviren katman. Hem çocuğun cihazında
 * (gösterim modunda) hem psikoloğun panelinde aynı metin üretilsin diye
 * burada duruyor: iki yerde iki ayrı yorum yazmak, zamanla iki farklı
 * ürün demek.
 *
 * TEK KURAL: norm iddia edilmez. "Normalin altında" gibi bir cümle bu
 * dosyada geçmez, çünkü karşılaştırılacak bir norm yok. Söylenen şey ne
 * ölçüldüğü ve dikkat çeken bir örüntü varsa onun ne olduğu.
 */

import type { Domain, ScoreRow, TaskCode } from './types'

export const DOMAIN_LABEL: Record<Domain, string> = {
  sustained_attention: 'Dikkati sürdürme',
  selective_attention: 'Seçici dikkat',
  working_memory_visual: 'Görsel işleyen bellek',
  working_memory_verbal: 'Sözel işleyen bellek',
  processing_speed: 'İşlem hızı',
  inhibition: 'Dürtü kontrolü',
  cognitive_flexibility: 'Bilişsel esneklik',
}

/** Raporda alanlar hep aynı sırada görünsün */
export const DOMAIN_ORDER: Domain[] = [
  'sustained_attention',
  'inhibition',
  'selective_attention',
  'working_memory_visual',
  'working_memory_verbal',
  'processing_speed',
  'cognitive_flexibility',
]

export const TASK_ADI: Record<TaskCode, string> = {
  cpt: 'Dikkati sürdürme oyunu',
  flanker: 'Ortadaki ok oyunu',
  span_visual: 'Kutu sırası oyunu',
  nback: 'Geriye bakma oyunu',
  symbol_search: 'Şekil arama oyunu',
  switching: 'Kural değiştirme oyunu',
}

/**
 * Ölçüt adı göreve göre değişir.
 *
 * "eşik" tek başına hiçbir şey anlatmıyor: flanker'da çeldirici bedeli,
 * kural değiştirmede geçiş bedeli, dizi görevinde kaç basamak demek.
 */
export function metricLabel(metric: string, task?: string): string {
  if (metric === 'threshold') {
    if (task === 'flanker') return 'çeldirici bedeli'
    if (task === 'switching') return 'geçiş bedeli'
    return 'eşik'
  }
  if (metric === 'span') return task === 'nback' ? 'kaç adım geri' : 'kaç basamak'
  if (metric === 'rt_mean') {
    return task === 'symbol_search' ? 'ortalama arama süresi' : 'ortalama tepki'
  }
  const m: Record<string, string> = {
    rt_sd: 'tepki değişkenliği',
    accuracy: 'doğruluk',
    d_prime: "ayırt etme (d')",
    commission_rate: 'yanlış basma',
    omission_rate: 'kaçırma',
  }
  return m[metric] ?? metric
}

export const BIRIM: Record<string, string> = {
  ms: 'ms',
  span: '',
  ratio: '',
  level: '',
  index: '',
}

/** ms tam sayı, d' ve basamak iki ondalık, oran yüzde */
export function bicim(v: number, unit: string, metric: string): string {
  if (unit === 'ratio') return '%' + Math.round(v * 100)
  if (unit === 'ms') return String(Math.round(v))
  if (metric === 'd_prime' || metric === 'span') return v.toFixed(2).replace('.', ',')
  return String(v).replace('.', ',')
}

/**
 * Alana dair düz Türkçe bir cümle.
 *
 * Yargı yok, ölçüm var. Dikkat çeken bir örüntü varsa (örneğin tepki
 * sürelerinin çok dalgalı olması) o belirtiliyor ama "şu kadar kötü"
 * denmiyor — karşılaştıracak norm olmadan böyle bir cümle kurmak
 * yanlış olur.
 */
export function yorumla(domain: string, rows: ScoreRow[]): string {
  const r = (m: string) => rows.find((x) => x.domain === domain && x.metric === m)?.value
  const span = r('span')
  const dPrime = r('d_prime')
  const rtMean = r('rt_mean')
  const rtSd = r('rt_sd')
  const acc = r('accuracy')
  const cost = r('threshold')
  const comm = r('commission_rate')

  const parca: string[] = []

  if (span !== undefined) {
    parca.push(
      domain === 'working_memory_verbal'
        ? `Ortalama ${span.toFixed(1).replace('.', ',')} adım geriyi hatırlayabildiği düzeyde çalıştı.`
        : `Dizi uzunluğunda ${span.toFixed(1).replace('.', ',')} basamak düzeyinde çalıştı.`,
    )
  }
  if (dPrime !== undefined) {
    parca.push(`Hedefi hedef olmayandan ayırt etme gücü d' = ${dPrime.toFixed(2).replace('.', ',')}.`)
  }
  if (acc !== undefined) parca.push(`Doğruluk %${Math.round(acc * 100)}.`)
  if (rtMean !== undefined) parca.push(`Ortalama tepki ${Math.round(rtMean)} ms.`)

  if (rtMean !== undefined && rtSd !== undefined && rtMean > 0) {
    parca.push(
      rtSd / rtMean > 0.35
        ? `Tepki süreleri dalgalı (değişkenlik ${Math.round(rtSd)} ms): bazen çok hızlı, bazen çok yavaş. Dikkatte süreklilik sorununda sık görülen bir örüntü.`
        : `Tepki süreleri istikrarlı (değişkenlik ${Math.round(rtSd)} ms).`,
    )
  }
  if (cost !== undefined && domain === 'selective_attention') {
    parca.push(
      cost > 15
        ? `Çeldirici bedeli ${Math.round(cost)} ms: çeldiriciyi bastırmak bu kadar yavaşlattı.`
        : 'Çeldiriciler onu yavaşlatmadı; dikkatini ortadaki oka iyi odaklamış.',
    )
  }
  if (cost !== undefined && domain === 'cognitive_flexibility') {
    parca.push(
      cost > 15
        ? `Geçiş bedeli ${Math.round(cost)} ms: kural değiştiğinde bu kadar yavaşladı.`
        : 'Kural değişimi onu yavaşlatmadı; esneklik açısından rahat görünüyor.',
    )
  }
  if (comm !== undefined && domain === 'inhibition') {
    parca.push(
      comm > 0.2
        ? `Basmaması gereken denemelerin %${Math.round(comm * 100)}'inde bastı; dürtü kontrolü açısından dikkat çekiyor.`
        : `Basmaması gereken denemelerde %${Math.round(comm * 100)} oranında basmış.`,
    )
  }

  return parca.length ? parca.join(' ') : 'Bu alanda yorumlanabilir veri üretilmedi.'
}

/**
 * Her raporun sonuna eklenen kısıtlar.
 *
 * İsteğe bağlı değil: ürünün GETAP'tan ayrıştığı yer tam burası. Ne
 * ölçtüğünü söylemek kadar, ne ölçmediğini söylemek de ürünün parçası.
 */
export const KISITLAR =
  'Bu bir tanı değildir ve yaşıt karşılaştırması içermez — henüz norm çalışması ' +
  'yapılmamıştır. Sayılar yalnızca kişinin kendi ölçümleri arasında karşılaştırılabilir. ' +
  'Tek bir ölçüm, çocuğun o günkü durumundan (uyku, kaygı, ilgi) etkilenir; yorum için ' +
  'en az iki ölçüm ve uygulayıcı gözlemi gerekir.'
